import { useEffect, useMemo } from 'react';

import { Line } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

import { useAppDispatch, useAppSelector } from '../../../../hooks';
import {
  type MeasurementPlane,
  removeExclusion,
  selectPlane,
} from '../../../../store/reducers/measurementPlanes.reducer.ts';
import { RENDER_LIFT_M, buildPlaneGeometry, polygonOutline } from '../../../../utils/measurementPlane/geometry.ts';

const FILL = '#ffd400'; // bright yellow — pops on dark scene and grey roads
const SELECTED = '#ff5e00'; // strong orange when selected
const EXCLUSION = '#ff3b3b'; // red — cut-out boundaries

function PlaneMesh({
  plane,
  selected,
  hasResult,
}: {
  plane: MeasurementPlane;
  selected: boolean;
  hasResult: boolean;
}): JSX.Element | null {
  const dispatch = useAppDispatch();
  const geometry = useMemo(
    () => buildPlaneGeometry(plane.polygon, plane.height, plane.frame, plane.exclusions),
    [plane.polygon, plane.height, plane.frame, plane.exclusions],
  );
  // Free the GPU buffers when the geometry is replaced (polygon/exclusion edit) or unmounts —
  // otherwise frequent editing leaks a BufferGeometry per change until a full page refresh.
  useEffect(() => () => geometry.dispose(), [geometry]);
  const outline = useMemo(
    () => polygonOutline(plane.polygon, plane.height, plane.frame),
    [plane.polygon, plane.height, plane.frame],
  );
  const exclusionOutlines = useMemo(
    () => (plane.exclusions ?? []).map((h) => polygonOutline(h, plane.height, plane.frame)),
    [plane.exclusions, plane.height, plane.frame],
  );
  const color = selected ? SELECTED : FILL;
  // Lift slightly off the surface — along the frame normal for oriented planes, else +Z.
  const lift: [number, number, number] = plane.frame
    ? [
        plane.frame.normal[0] * RENDER_LIFT_M,
        plane.frame.normal[1] * RENDER_LIFT_M,
        plane.frame.normal[2] * RENDER_LIFT_M,
      ]
    : [0, 0, RENDER_LIFT_M];

  if (plane.polygon.length < 3) return null;

  return (
    <group position={lift}>
      {/* Flat fill is only a drawing affordance shown until the surface is computed —
          once results are in, the draped measurement points are the surface, so we
          never show a flat plane inside the polygon. It also carries hover/select. */}
      {!hasResult && (
        <mesh
          geometry={geometry}
          onClick={(e: ThreeEvent<MouseEvent>) => (e.stopPropagation(), dispatch(selectPlane(plane.id)))}
        >
          <meshBasicMaterial color={color} transparent opacity={0.22} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
      )}
      <Line points={outline} color={color} lineWidth={selected ? 4 : 3} depthTest={false} />
      {/* Cut-out boundaries — click to remove that hole. */}
      {exclusionOutlines.map((pts, i) => (
        <Line
          key={i}
          points={pts}
          color={EXCLUSION}
          lineWidth={selected ? 3.5 : 2.5}
          depthTest={false}
          onClick={(e: ThreeEvent<MouseEvent>) => (
            e.stopPropagation(),
            dispatch(removeExclusion({ planeId: plane.id, index: i }))
          )}
        />
      ))}
    </group>
  );
}

/** Renders every committed measurement plane: outline always, plus a flat fill only
 *  before its draped heatmap is computed. */
export function MeasurementPlanesRenderer(): JSX.Element {
  const planes = useAppSelector((s) => s.measurementPlanes.planes);
  const selectedId = useAppSelector((s) => s.measurementPlanes.selectedId);
  const results = useAppSelector((s) => s.measurementPlanes.results);

  return (
    <group>
      {planes
        .filter((p) => p.visible)
        .map((p) => (
          <PlaneMesh
            key={p.id}
            plane={p}
            selected={p.id === selectedId}
            hasResult={results.some((r) => r.uuid === p.id)}
          />
        ))}
    </group>
  );
}
